import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import CallToAction from '../components/Card/CallToAction';
import { HUMAN_DATE_FORMAT } from '../utils/constants';
import { useLoadingToast } from '../utils/useLoadingToast';
import { nextBillingDate } from './api';
import SubsBillingSkip from './SubsBillingSkip';
import { useGlobalContext } from './state/GlobalContext';
import Card from '../components/Card';

import 'react-datepicker/dist/react-datepicker.css';
import { DatePicker } from '../components/DatePicker/DatePicker';
import { isDateValidByBatching } from '../utils/batching';

dayjs.extend(utc);
dayjs.extend(timezone);

const NBDContainer = styled.div.attrs({
  className: 'awt-nbd-code-container',
})`
  display: flex;
  padding-top: 0;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 1rem;

  .bundle-button {
    padding: 0;
    margin: 0;
    min-width: 0;
  }
`;

interface IProps {
  subscription: Subscription;
  isBatched: boolean;
  onSkipUpdate: (msg: string) => void;
  onUpdate: (res: Subscription, msg: string, isUpdatingDate?: boolean) => void;
  onError: (msg: string) => void;
  showReactivate?: boolean;
  isPrepaid: boolean;
  isBillingOrUpdating?: boolean;
  shopConfiguration?: ShopConfiguration;
}
export default ({
  subscription,
  onSkipUpdate,
  onError,
  onUpdate,
  showReactivate,
  isBillingOrUpdating,
  shopConfiguration,
}: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { customerId, subscriptionId, authToken, customerPortalSettings } =
    useGlobalContext();
  const [datePickerValue, setDatePickerValue] = useState<Dayjs>(
    dayjs(subscription?.NextBillingDate),
  );
  const [showEdit, setShowEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const { addLoadingToast, removeLoadingToast } = useLoadingToast();

  const isActive = subscription?.SubscriptionStatus === 'ACTIVE';
  const isPaused = subscription?.SubscriptionStatus === 'PAUSED';
  const isCancelled = subscription?.SubscriptionStatus === 'CANCELLED';
  const isExpired = subscription?.SubscriptionStatus === 'EXPIRED';
  const hideSkipNext =
    customerPortalSettings?.hideReschedule ||
    customerPortalSettings?.hideSkipNext;
  const hideEditNextOrderDate =
    customerPortalSettings?.hideReschedule ||
    customerPortalSettings?.hideEditNextOrderDate;
  const batchingSettings = subscription?.BatchingConfiguration;

  const showSkip = isActive && !hideSkipNext;

  const handleDatePickerChange = (value: string | Date) => {
    setDatePickerValue(dayjs(value));
  };

  const assignNewDate = async (
    date: dayjs.Dayjs,
    success: string,
    error: string,
  ) => {
    const isUpdatingDate = true;
    try {
      setLoading(true);
      addLoadingToast();
      const res = await nextBillingDate(
        date,
        customerId,
        subscriptionId,
        authToken,
        'UPDATED_ORDER_DATE',
      );
      onUpdate(
        res,
        t(success, {
          date: date.format(HUMAN_DATE_FORMAT),
        }),
        isUpdatingDate,
      );
      setDatePickerValue(dayjs(res.NextBillingDate));
      setShowEdit(false);
    } catch (err) {
      console.error(err);
      onError(t(error));
    } finally {
      setLoading(false);
      removeLoadingToast();
    }
  };

  const handleDateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    assignNewDate(
      dayjs(datePickerValue),
      'subscription.next_order_success',
      'subscription.next_order_error',
    ).then();
  };

  const handleReactivation = () => {
    const date = dayjs().utc().add(1, 'day');
    assignNewDate(
      date,
      'subscription.reactivate_success',
      'subscription.reactivate_error',
    ).then();
  };

  const today = dayjs().tz(dayjs.tz.guess());

  return (
    <Card
      ariaLabel={
        !showEdit
          ? t('aria_labels.edit_next_order')
          : t('aria_labels.cancel_edit_next_order')
      }
      headerText={t('tables.next_order')}
      btnText={!showEdit ? t('titles.edit') : t('titles.cancel')}
      onClick={() => setShowEdit((showEdit) => !showEdit)}
      className="bundle-card--next-order-date"
      showButton={isActive && !hideEditNextOrderDate}
    >
      <NBDContainer>
        {!showEdit && (
          <>
            {isActive && (
              <>
                {subscription?.OriginalNextBillingDate ? (
                  <span>
                    {new Date(
                      subscription?.OriginalNextBillingDate,
                    ).toLocaleDateString()}
                    <span> ({t('subscription.delayed')})</span>
                  </span>
                ) : subscription?.NextBillingDate ? (
                  <span>
                    {new Date(
                      subscription?.NextBillingDate,
                    ).toLocaleDateString()}
                  </span>
                ) : null}
                {showSkip && (
                  <>
                    <span>{' · '}</span>
                    <SubsBillingSkip
                      subscription={subscription}
                      onUpdate={onSkipUpdate}
                      onError={onError}
                      isBillingOrUpdating={isBillingOrUpdating}
                      shopConfiguration={shopConfiguration}
                    />
                  </>
                )}
              </>
            )}
            {isPaused
              ? t('subscription.paused')
              : isCancelled
                ? t('subscription.cancelled')
                : isExpired
                  ? t('subscription.expired')
                  : ''}
            {isPaused && showReactivate && (
              <button
                className="bundleapp-btn--text"
                onClick={handleReactivation}
              >
                {t('subscription.reactivate_btn')}
              </button>
            )}
          </>
        )}
      </NBDContainer>

      {showEdit && (
        <NBDContainer>
          <form onSubmit={handleDateSubmit}>
            <div className="bundleapp-flex" style={{ marginBottom: 10 }}>
              <DatePicker
                timezone={shopConfiguration?.ShopDomainTZ}
                filterDate={(date) =>
                  isDateValidByBatching({ date, batchingSettings })
                }
                value={datePickerValue.toDate()}
                placeholderText={datePickerValue.format('DD/MM/YYYY')}
                onChange={handleDatePickerChange}
                minDate={today.add(1, 'day').toDate()}
              />
            </div>
            <CallToAction
              type="submit"
              onClick={handleDateSubmit}
              isDisabled={loading}
              isLoading={loading}
            >
              {t('titles.save')}
            </CallToAction>
          </form>
        </NBDContainer>
      )}
    </Card>
  );
};
